import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '../stores/auth'
import type { FeatureModule } from '../types/featureModules'
import HomeView from '../views/HomeView.vue'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      name: 'home',
      component: HomeView,
      meta: { requiresAuth: true, requiredModule: 'sentinel' },
    },
    {
      path: '/login',
      name: 'login',
      component: () => import('../views/Auth/Login.vue'),
      meta: { guest: true },
    },
    {
      path: '/register',
      name: 'register',
      component: () => import('../views/Auth/Register.vue'),
      meta: { guest: true },
    },
    {
      path: '/forgot-password',
      name: 'forgot-password',
      component: () => import('../views/Auth/ForgotPassword.vue'),
      meta: { guest: true, title: 'Forgot Password' },
    },
    {
      path: '/reset-password/:token',
      name: 'reset-password',
      component: () => import('../views/Auth/ResetPassword.vue'),
      meta: { guest: true, title: 'Reset Password' },
    },
    {
      path: '/calculators/module-a',
      name: 'module-a',
      component: () => import('../views/Calculators/ModuleAView.vue'),
      meta: { title: 'Auto Lot Sizing', requiresAuth: true, requiredModule: 'trading' }
    },
    {
      path: '/calculators/module-b',
      name: 'module-b',
      component: () => import('../views/Calculators/ModuleBView.vue'),
      meta: { title: 'Manual Lot Sizing', requiresAuth: true, requiredModule: 'trading' }
    },
    {
      path: '/tools/max-levels',
      name: 'max-levels',
      component: () => import('../views/Tools/MaxLevelsView.vue'),
      meta: { title: 'Risk Analyzer', requiresAuth: true, requiredModule: 'trading' }
    },
    {
      path: '/sentinel',
      name: 'sentinel-dashboard',
      component: () => import('../views/Sentinel/DashboardView.vue'),
      meta: { title: 'SENTINEL Dashboard', requiresAuth: true, requiredModule: 'sentinel' }
    },
    {
      path: '/sentinel/signals',
      name: 'sentinel-signals',
      component: () => import('../views/Sentinel/SignalFeedView.vue'),
      meta: { title: 'Signal Feed', requiresAuth: true, requiredModule: 'sentinel' }
    },
    {
      path: '/sentinel/alerts',
      name: 'sentinel-alerts',
      component: () => import('../views/Sentinel/AlertRulesView.vue'),
      meta: { title: 'Alert Rules', requiresAuth: true, requiredModule: 'sentinel' }
    },
    {
      path: '/sentinel/sources',
      name: 'sentinel-sources',
      component: () => import('../views/Sentinel/SourceManagementView.vue'),
      meta: { title: 'Source Management', requiresAuth: true, requiresAdmin: true },
    },
    {
      path: '/sentinel/situations/:id',
      name: 'SituationDetail',
      component: () => import('../views/Sentinel/SituationDetailView.vue'),
      meta: { requiresAuth: true, requiredModule: 'sentinel' },
    },
    {
      path: '/admin/situations',
      name: 'AdminSituations',
      component: () => import('../views/Sentinel/SituationsAdminView.vue'),
      meta: { requiresAuth: true, requiresAdmin: true },
    },
    {
      path: '/admin/users',
      name: 'AdminUsers',
      component: () => import('../views/Admin/UserManagementView.vue'),
      meta: { requiresAuth: true, requiresAdmin: true, title: 'User Management' },
    },
    {
      path: '/admin/licenses',
      name: 'AdminRecoveryLicenses',
      component: () => import('../views/Admin/RecoveryLicensesView.vue'),
      meta: { requiresAuth: true, requiresAdmin: true, title: 'RecoveryEA Licenses' },
    },
    {
      path: '/documentation',
      name: 'Documentation',
      component: () => import('../views/Documentation/DocumentationView.vue'),
      meta: { requiresAuth: true, requiredModule: 'documentation', title: 'Documentation' },
    },
    {
      path: '/mt4/dashboard',
      name: 'mt4-dashboard',
      component: () => import('../views/Mt4/DashboardView.vue'),
      meta: { title: 'MT4 Trade Dashboard', requiresAuth: true, requiredModule: 'trading' }
    },
    {
      path: '/mt4/api-keys',
      name: 'mt4-api-keys',
      component: () => import('../views/Mt4/ApiKeysView.vue'),
      meta: { title: 'MT4 API Keys', requiresAuth: true, requiredModule: 'trading' }
    },
    {
      path: '/finance/budget-tracker',
      name: 'budget-tracker',
      component: () => import('../views/Finance/BudgetTrackerView.vue'),
      meta: { title: 'Budget Tracker', requiresAuth: true, requiredModule: 'finance' }
    },
    {
      path: '/finance/paystub-tax',
      name: 'paystub-tax',
      component: () => import('../views/Finance/PaystubTaxView.vue'),
      meta: { title: 'Paystub & Tax Dashboard', requiresAuth: true, requiredModule: 'finance' }
    },
    {
      path: '/todos',
      name: 'todos',
      component: () => import('../views/Todos/TodosView.vue'),
      meta: { title: 'Tasks', requiresAuth: true, requiredModule: 'productivity' }
    },
    {
      path: '/productivity/dashboard',
      name: 'productivity-dashboard',
      component: () => import('../views/Productivity/LifeDashboardView.vue'),
      meta: { title: 'Dashboard', requiresAuth: true, requiredModule: 'productivity' }
    },
    {
      path: '/productivity/insights',
      name: 'productivity-insights',
      component: () => import('../views/Productivity/ProductivityInsightsView.vue'),
      meta: { title: 'Productivity Analytics', requiresAuth: true, requiredModule: 'productivity' }
    },
    {
      path: '/productivity/projects',
      name: 'productivity-projects',
      component: () => import('../views/Productivity/ProjectsView.vue'),
      meta: { title: 'Projects', requiresAuth: true, requiredModule: 'productivity' }
    },
    {
      path: '/productivity/routines',
      name: 'productivity-routines',
      component: () => import('../views/Productivity/RoutinesView.vue'),
      meta: { title: 'Routines', requiresAuth: true, requiredModule: 'productivity' }
    },
    {
      path: '/productivity/life-areas',
      name: 'productivity-life-areas',
      component: () => import('../views/Productivity/LifeAreasView.vue'),
      meta: { title: 'Life Areas', requiresAuth: true, requiredModule: 'productivity' }
    },
    {
      path: '/productivity/review',
      name: 'productivity-review',
      component: () => import('../views/Productivity/WeeklyReviewView.vue'),
      meta: { title: 'Weekly Review', requiresAuth: true, requiredModule: 'productivity' }
    },
    {
      path: '/access-pending',
      name: 'access-pending',
      component: () => import('../views/Auth/AccessPendingView.vue'),
      meta: { title: 'Access Pending', requiresAuth: true },
    },
    {
      path: '/about',
      name: 'about',
      component: () => import('../views/AboutView.vue'),
      meta: { requiresAuth: true },
    },
  ],
})

router.beforeEach(async (to) => {
  const auth = useAuthStore()

  if (auth.token && auth.user && !auth.permissionsSynced) {
    const result = await auth.fetchUser()
    if (!result.success) {
      return { name: 'login', query: { redirect: to.fullPath } }
    }
  }

  if (to.meta.requiresAuth && !auth.isAuthenticated) {
    return { name: 'login', query: { redirect: to.fullPath } }
  }

  if (to.meta.requiresAdmin && !auth.isAdmin) {
    return auth.defaultRoute
  }

  const requiredModule = to.meta.requiredModule as FeatureModule | undefined
  if (requiredModule && !auth.canAccessModule(requiredModule)) {
    return { name: 'access-pending', query: { denied: requiredModule } }
  }

  if (to.meta.guest && auth.isAuthenticated) {
    return auth.defaultRoute
  }

  if (to.name === 'access-pending' && !to.query.denied && (auth.isAdmin || auth.user?.modules.length)) {
    return auth.defaultRoute
  }
})

export default router
